import { createContext, use } from "react";
import type { AuthPort, Session } from "../../services/auth/port";

/**
 * Split out from AuthProvider.tsx so that file exports only a component.
 * Mixing hooks and components in one module breaks Vite's fast refresh — the
 * whole tree remounts on every edit, which in this app means losing your session
 * and your chat thread on each keystroke.
 */

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  /** Redirect-based: hands the browser to Managed Login and does not return.
      There is deliberately no `login(email, password)` — see port.ts. */
  beginSignIn: AuthPort["beginSignIn"];
  /** Only /auth/callback calls this. */
  completeSignIn: AuthPort["completeSignIn"];
  logout: AuthPort["logout"];
  /** Forces a token re-read after the SERVER changed something the session is
      built from — today, the 18+ claim. See AuthPort.refreshSession. */
  refreshSession: AuthPort["refreshSession"];
  /** Record that the SERVER confirmed an 18+ declaration, so the guard opens
      even if the claim has not reached this tab's token yet. In-memory only —
      see AuthProvider for why the claim alone is not enough. */
  markAdultDeclared: () => void;
  resendVerification: AuthPort["resendVerification"];
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
